import { useMemo, useState, useRef, useEffect } from "react";
import {
  CaretUp,
  CaretDown,
  MagnifyingGlass,
  XCircle,
} from "phosphor-react";
import { bucketUrlPrefix } from "common/config";
import { ComplexCategoriesData, TaggingCategoryData } from "common/types";
import { VenueDetailsBySlugResponse, useI18N, useZoomDetection } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { useDarkMode } from "@/hooks/useDarkMode";

type CategorySidebarProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: number | null;
  setSelectedCategory: (categoryId: number | null) => void;
  expandedCategories: Set<number>;
  setExpandedCategories: React.Dispatch<React.SetStateAction<Set<number>>>;
  showMobileMenu: boolean;
  setShowMobileMenu: (show: boolean) => void;
  onCategorySelect?: (categoryId: number, categoryName: string) => void;
  searchPlaceholder?: string;
  filteredReviewTags?: (ComplexCategoriesData | TaggingCategoryData)[];
  pageName?: string; // For analytics tracking
  filterSlot?: React.ReactNode; // Optional filter buttons rendered between search and category tree
};

function CategorySidebar({
  venueDetailsResponse,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  expandedCategories,
  setExpandedCategories,
  showMobileMenu,
  setShowMobileMenu,
  onCategorySelect,
  searchPlaceholder = "Search Space name",
  filteredReviewTags,
  pageName = "Accessibility Guide",
  filterSlot,
}: CategorySidebarProps) {
  const i18n = useI18N();
  const isDark = useDarkMode();

  // Determine event names based on pageName
  const getEventName = (baseName: string) => {
    if (pageName === "Accessibility Space Guide") {
      const eventMap: Record<string, string> = {
        "accessibilityGuideSearchPerformed": "accessibilitySpaceGuideSearchPerformed",
        "accessibilityGuideSearchCleared": "accessibilitySpaceGuideSearchCleared",
        "accessibilityGuideCategoryClicked": "accessibilitySpaceGuideCategoryClicked",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };

  // Debounced search tracking
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isZoomedIn = useZoomDetection(1.25);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Track search after 500ms of inactivity
    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        const eventName = getEventName("accessibilityGuideSearchPerformed");
        AnalyticsService.trackGAEventHandler(eventName);
        AnalyticsService.trackMx(eventName, {
          SearchTerm: value,
        });
      }, 500);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Use provided filteredReviewTags or fall back to all review tags
  const reviewTags = useMemo(
    () => filteredReviewTags || venueDetailsResponse?.review_tags || [],
    [filteredReviewTags, venueDetailsResponse?.review_tags],
  );

  // Auto-expand categories when search matches lower-level items
  useEffect(() => {
    const query = searchTerm.toLowerCase().trim();
    
    if (query && reviewTags.length > 0) {
      // Collect all category IDs that should be expanded based on search results
      const categoriesToExpand = new Set<number>();
      
      reviewTags.forEach((category) => {
        // Always expand top-level category if it has filtered content
        if (category.categoryOptions && category.categoryOptions.length > 0) {
          categoriesToExpand.add(category.id);
          
          // Check if any second-level or third-level categories have content
          category.categoryOptions.forEach((option) => {
            if (option.pageChildrenCategories && option.pageChildrenCategories.length > 0) {
              // Expand the second level if it has matching subcategories
              if (option.pageTitleId) {
                categoriesToExpand.add(option.pageTitleId);
              }
            }
          });
        }
      });
      
      // Only update categories that aren't already expanded
      const hasNewCategories = Array.from(categoriesToExpand).some(
        (catId) => !expandedCategories.has(catId)
      );
      
      if (hasNewCategories) {
        setExpandedCategories((prev) => {
          const newSet = new Set(prev);
          categoriesToExpand.forEach((catId) => newSet.add(catId));
          return newSet;
        });
      }
    }
    // We only want to react to search term and filtered results changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, reviewTags]);

  // Roving tabindex state for tree navigation
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);
  const treeItemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Helper to collect all visible tree item IDs in DOM order
  const getVisibleItemIds = useMemo(() => {
    const ids: number[] = [];
    
    const collectIds = (
      category: ComplexCategoriesData | TaggingCategoryData,
      skipDuplicates: boolean = false
    ) => {
      ids.push(category.id);
      
      if (expandedCategories.has(category.id) && category.categoryOptions) {
        category.categoryOptions.forEach((option) => {
          const shouldSkipLevel = option.pageTitle === category.name;
          
          if (shouldSkipLevel && option.pageChildrenCategories) {
            // Skip this level, render children directly
            option.pageChildrenCategories.forEach((subCategory) => {
              collectIds(subCategory, false);
            });
          } else {
            // Normal rendering
            ids.push(option.pageTitleId);
            
            if (expandedCategories.has(option.pageTitleId) && option.pageChildrenCategories) {
              option.pageChildrenCategories
                .filter((subCategory) => subCategory.name !== option.pageTitle)
                .forEach((subCategory) => {
                  collectIds(subCategory, false);
                });
            }
          }
        });
      }
    };
    
    reviewTags.forEach((category) => collectIds(category));
    return ids;
  }, [reviewTags, expandedCategories]);

  // Helper functions for category management
  const toggleExpanded = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Helper to close mobile menu only on mobile screens
  const closeMobileMenuIfMobile = () => {
    // Check if screen is mobile (below md breakpoint: 768px)
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  const scrollToCategory = (categoryName: string) => {
    setTimeout(() => {
      const categoryId = `category-${categoryName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
      const element = document.getElementById(categoryId);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 100);
  };

  // Tree keyboard navigation handler
  const handleTreeKeyDown = (e: React.KeyboardEvent) => {
    const visibleIds = getVisibleItemIds;
    const currentIndex = focusedItemId !== null ? visibleIds.indexOf(focusedItemId) : -1;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < visibleIds.length - 1) {
          const nextId = visibleIds[currentIndex + 1];
          setFocusedItemId(nextId);
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          const prevId = visibleIds[currentIndex - 1];
          setFocusedItemId(prevId);
        }
        break;
        
      case 'ArrowRight':
        e.preventDefault();
        if (focusedItemId !== null) {
          if (expandedCategories.has(focusedItemId)) {
            // Already expanded - move to first child
            if (currentIndex < visibleIds.length - 1) {
              const nextId = visibleIds[currentIndex + 1];
              setFocusedItemId(nextId);
            }
          } else {
            // Expand this category
            toggleExpanded(focusedItemId);
          }
        }
        break;
        
      case 'ArrowLeft':
        e.preventDefault();
        if (focusedItemId !== null && expandedCategories.has(focusedItemId)) {
          // Collapse if expanded
          toggleExpanded(focusedItemId);
        }
        break;
        
      case 'Home':
        e.preventDefault();
        if (visibleIds.length > 0) {
          setFocusedItemId(visibleIds[0]);
        }
        break;
        
      case 'End':
        e.preventDefault();
        if (visibleIds.length > 0) {
          setFocusedItemId(visibleIds[visibleIds.length - 1]);
        }
        break;
        
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedItemId !== null) {
          const ref = treeItemRefs.current.get(focusedItemId);
          if (ref) {
            ref.click();
          }
        }
        break;
    }
  };

  // Focus the focused item when it changes
  useEffect(() => {
    if (focusedItemId !== null) {
      const ref = treeItemRefs.current.get(focusedItemId);
      if (ref) {
        ref.focus();
      }
    }
  }, [focusedItemId]);

  // Set initial focus to first item when tree is mounted
  useEffect(() => {
    if (focusedItemId === null && getVisibleItemIds.length > 0) {
      setFocusedItemId(getVisibleItemIds[0]);
    }
  }, [focusedItemId, getVisibleItemIds]);

  // Recursive component for rendering nested categories
  const renderCategory = (
    category: ComplexCategoriesData | TaggingCategoryData,
    level: number = 0,
  ) => {
    // Check if category has children - always show expand if categoryOptions exist
    // The actual filtering of duplicates happens during rendering
    const hasChildren =
      category.categoryOptions && category.categoryOptions.length > 0;
    const paddingLeft =
      level === 0 ? "pl-4" : `pl-[${52 + (level - 1) * 24}px]`;
    const verticalBarLeft =
      level === 0 ? "left-[27px]" : `left-[${27 + level * 24}px]`;

    return (
      <div key={category.id} className="relative">
        {/* Category Item */}
        <button
          type="button"
          role="treeitem"
          aria-level={level + 1}
          aria-expanded={hasChildren ? expandedCategories.has(category.id) : undefined}
          aria-selected={selectedCategory === category.id}
          tabIndex={focusedItemId === category.id ? 0 : -1}
          ref={(el) => {
            if (el) {
              treeItemRefs.current.set(category.id, el);
            } else {
              treeItemRefs.current.delete(category.id);
            }
          }}
          className={`flex items-center gap-3 ${paddingLeft} pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
            selectedCategory === category.id ? "bg-container-brand-minimal" : ""
          }`}
          onClick={() => {
            // Analytics tracking
            const eventName = getEventName("accessibilityGuideCategoryClicked");
            AnalyticsService.trackGAEventHandler(eventName);
            AnalyticsService.trackMx(eventName, {
              CategoryName: category.name,
              CategoryId: category.id,
              CategoryLevel: level,
            });
            
            setFocusedItemId(category.id);
            setSelectedCategory(category.id);
            if (hasChildren) {
              // If category has children, just expand it without navigating
              toggleExpanded(category.id);
            } else {
              // Only navigate and close menu if category has no children (leaf node)
              scrollToCategory(category.name);
              closeMobileMenuIfMobile();
              onCategorySelect?.(category.id, category.name);
            }
          }}
          aria-label={category.name}
        >
          {/* Category Icon - only show for top level */}
          {level === 0 && category.image && (
            <img
              src={
                bucketUrlPrefix +
                (isDark && category.image_selected
                  ? category.image_selected
                  : category.image)
              }
              alt={category.name}
              className={`w-6 h-6 flex-shrink-0 ${isDark && !category.image_selected ? "filter brightness-0 invert" : ""}`}
              aria-hidden="true"
            />
          )}

          {/* Category Name */}
          <span
            className={`flex-1 ${level === 0 ? "label-m-semibold" : "label-m-medium"} ${
              selectedCategory === category.id
                ? "text-fg-brand-subtle"
                : "text-fg-neutral-strong"
            }`}
          >
            {category.name}
          </span>

          {/* Expand/Collapse Button */}
          {hasChildren && (
            expandedCategories.has(category.id)
              ? <CaretUp size={16} className="text-fg-neutral-strong" aria-hidden="true" />
              : <CaretDown size={16} className="text-fg-neutral-strong" aria-hidden="true" />
          )}
        </button>

        {/* Subcategories */}
        {expandedCategories.has(category.id) && category.categoryOptions && (
          <div role="group" className="bg-bg-brand-secondary-default relative">
            {/* Vertical line for subcategories - always show for level 0 and above */}
            {level >= 0 && (
              <div
                className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] `}
              ></div>
            )}
            {category.categoryOptions.map((categoryOption) => {
              // Skip this level if the pageTitle matches the parent category name
              const shouldSkipLevel =
                categoryOption.pageTitle === category.name;

              // If we should skip this level, render children directly
              if (shouldSkipLevel && categoryOption.pageChildrenCategories) {
                return categoryOption.pageChildrenCategories.map(
                  (subCategory) => (
                    <div key={subCategory.id} className="relative group">
                      {/* Vertical bar highlight */}
                      <div
                        className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] transition-colors ${
                          selectedCategory === subCategory.id
                            ? "bg-[#352FC3]"
                            : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                        }`}
                      ></div>
                      <button
                        type="button"
                        role="treeitem"
                        aria-level={level + 2}
                        aria-expanded={
                          subCategory.categoryOptions && subCategory.categoryOptions.length > 0
                            ? expandedCategories.has(subCategory.id)
                            : undefined
                        }
                        aria-selected={selectedCategory === subCategory.id}
                        tabIndex={focusedItemId === subCategory.id ? 0 : -1}
                        ref={(el) => {
                          if (el) {
                            treeItemRefs.current.set(subCategory.id, el);
                          } else {
                            treeItemRefs.current.delete(subCategory.id);
                          }
                        }}
                        className={`flex items-center gap-3 pl-[52px] pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
                          selectedCategory === subCategory.id
                            ? "bg-container-brand-minimal"
                            : "bg-bg-brand-secondary-default"
                        }`}
                        onClick={() => {
                          setFocusedItemId(subCategory.id);
                          setSelectedCategory(subCategory.id);
                          const hasChildren =
                            subCategory.categoryOptions &&
                            subCategory.categoryOptions.length > 0;

                          if (hasChildren) {
                            // If has children, just expand without navigating
                            toggleExpanded(subCategory.id);
                          } else {
                            // Only navigate and close menu if no children (leaf node)
                            scrollToCategory(subCategory.name);
                            closeMobileMenuIfMobile();
                            onCategorySelect?.(
                              subCategory.id,
                              subCategory.name,
                            );
                          }
                        }}
                        aria-label={subCategory.name}
                      >
                        <div
                          className={`absolute left-[40px] top-0 bottom-0 w-[2px] transition-colors ${
                            selectedCategory === subCategory.id
                              ? "bg-[#352FC3]"
                              : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                          }`}
                        ></div>
                        <span
                          className={`flex-1 label-m-medium ${
                            selectedCategory === subCategory.id
                              ? "text-fg-brand-subtle"
                              : "text-fg-neutral-strong"
                          }`}
                        >
                          {subCategory.name}
                        </span>
                        {subCategory.categoryOptions &&
                          subCategory.categoryOptions.length > 0 && (
                            expandedCategories.has(subCategory.id)
                              ? <CaretUp size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                              : <CaretDown size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                          )}
                      </button>
                      {expandedCategories.has(subCategory.id) &&
                        subCategory.categoryOptions && (
                          <div role="group" className="bg-bg-brand-secondary-default">
                            {renderCategory(subCategory, level + 1)}
                          </div>
                        )}
                    </div>
                  ),
                );
              }

              // Normal rendering if not skipping
              return (
                <div key={categoryOption.pageTitleId}>
                  {/* Level 2: Category Option (pageTitle) */}
                  <button
                    type="button"
                    role="treeitem"
                    aria-level={level + 2}
                    aria-expanded={
                      categoryOption.pageChildrenCategories &&
                      categoryOption.pageChildrenCategories.filter(
                        (subCategory) => subCategory.name !== categoryOption.pageTitle
                      ).length > 0
                        ? expandedCategories.has(categoryOption.pageTitleId)
                        : undefined
                    }
                    aria-selected={selectedCategory === categoryOption.pageTitleId}
                    tabIndex={focusedItemId === categoryOption.pageTitleId ? 0 : -1}
                    ref={(el) => {
                      if (el) {
                        treeItemRefs.current.set(categoryOption.pageTitleId, el);
                      } else {
                        treeItemRefs.current.delete(categoryOption.pageTitleId);
                      }
                    }}
                    className={`flex items-center gap-3 pl-[52px] pr-4 py-3 relative group w-full text-left transition-colors focus-blue ${
                      selectedCategory === categoryOption.pageTitleId
                        ? "bg-container-brand-minimal"
                        : "bg-bg-brand-secondary-default"
                    }`}
                    onClick={() => {
                      setFocusedItemId(categoryOption.pageTitleId);
                      setSelectedCategory(categoryOption.pageTitleId);
                      // Check if this level has actual children (after filtering duplicates)
                      const hasNonDuplicateChildren =
                        categoryOption.pageChildrenCategories &&
                        categoryOption.pageChildrenCategories.filter(
                          (subCategory) =>
                            subCategory.name !== categoryOption.pageTitle,
                        ).length > 0;

                      if (hasNonDuplicateChildren) {
                        // If has children, just expand without navigating
                        toggleExpanded(categoryOption.pageTitleId);
                      } else {
                        // Only navigate and close menu if no children (leaf node)
                        scrollToCategory(categoryOption.pageTitle);
                        closeMobileMenuIfMobile();
                        onCategorySelect?.(
                          categoryOption.pageTitleId,
                          categoryOption.pageTitle,
                        );
                      }
                    }}
                    aria-label={categoryOption.pageTitle}
                  >
                    {/* Vertical bar highlight on hover and selected */}
                    <div
                      className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] transition-colors ${
                        selectedCategory === categoryOption.pageTitleId
                          ? "bg-[#352FC3]"
                          : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                      }`}
                    ></div>

                    {/* Category Option Name */}
                    <span
                      className={`flex-1 label-m-medium ${
                        selectedCategory === categoryOption.pageTitleId
                          ? "text-fg-brand-subtle"
                          : "text-fg-neutral-strong"
                      }`}
                    >
                      {categoryOption.pageTitle}
                    </span>

                    {/* Expand/Collapse Button for Level 2 - only show if there are non-duplicate children */}
                    {categoryOption.pageChildrenCategories &&
                      categoryOption.pageChildrenCategories.filter(
                        (subCategory) =>
                          subCategory.name !== categoryOption.pageTitle,
                      ).length > 0 && (
                        expandedCategories.has(categoryOption.pageTitleId)
                          ? <CaretUp size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                          : <CaretDown size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                      )}
                  </button>

                  {/* Level 3: Page Children Categories */}
                  {expandedCategories.has(categoryOption.pageTitleId) &&
                    categoryOption.pageChildrenCategories && (
                      <div role="group" className="bg-bg-brand-secondary-default relative">
                        {/* Vertical line for level 3 */}
                        <div className="absolute left-[51px] top-0 bottom-0 w-[2px] bg-[#DEDDF7]"></div>
                        {categoryOption.pageChildrenCategories
                          .filter(
                            (subCategory) =>
                              subCategory.name !== categoryOption.pageTitle,
                          )
                          .map((subCategory) => (
                            <div
                              key={subCategory.id}
                              className="relative group"
                            >
                              {/* Vertical bar highlight on hover and selected for level 3 */}
                              <div
                                className={`absolute left-[51px] top-0 bottom-0 w-[2px] transition-colors ${
                                  selectedCategory === subCategory.id
                                    ? "bg-[#352FC3]"
                                    : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                                }`}
                              ></div>
                              {/* Level 3 Category Item */}
                              <button
                                type="button"
                                role="treeitem"
                                aria-level={level + 3}
                                aria-expanded={
                                  subCategory.categoryOptions && subCategory.categoryOptions.length > 0
                                    ? expandedCategories.has(subCategory.id)
                                    : undefined
                                }
                                aria-selected={selectedCategory === subCategory.id}
                                tabIndex={focusedItemId === subCategory.id ? 0 : -1}
                                ref={(el) => {
                                  if (el) {
                                    treeItemRefs.current.set(subCategory.id, el);
                                  } else {
                                    treeItemRefs.current.delete(subCategory.id);
                                  }
                                }}
                                className={`flex items-center gap-3 pl-[76px] pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
                                  selectedCategory === subCategory.id
                                    ? "bg-container-brand-minimal"
                                    : "bg-bg-brand-secondary-default"
                                }`}
                                onClick={() => {
                                  setFocusedItemId(subCategory.id);
                                  setSelectedCategory(subCategory.id);
                                  const hasChildren =
                                    subCategory.categoryOptions &&
                                    subCategory.categoryOptions.length > 0;

                                  if (hasChildren) {
                                    // If has children, just expand without navigating
                                    toggleExpanded(subCategory.id);
                                  } else {
                                    // Only navigate and close menu if no children (leaf node)
                                    scrollToCategory(subCategory.name);
                                    closeMobileMenuIfMobile();
                                    onCategorySelect?.(
                                      subCategory.id,
                                      subCategory.name,
                                    );
                                  }
                                }}
                                aria-label={subCategory.name}
                              >
                                {/* Light blue vertical bar for all 3rd level categories with hover and selected effects */}
                                <div
                                  className={`absolute left-[64px] top-0 bottom-0 w-[2px] transition-colors ${
                                    selectedCategory === subCategory.id
                                      ? "bg-[#352FC3]"
                                      : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                                  }`}
                                ></div>
                                {/* Category Name */}
                                <span
                                  className={`flex-1 label-m-medium ${
                                    selectedCategory === subCategory.id
                                      ? "text-fg-brand-subtle"
                                      : "text-fg-neutral-strong"
                                  }`}
                                >
                                  {subCategory.name}
                                </span>

                                {/* Expand/Collapse Button for Level 3 if it has children */}
                                {subCategory.categoryOptions &&
                                  subCategory.categoryOptions.length > 0 && (
                                    expandedCategories.has(subCategory.id)
                                      ? <CaretUp size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                                      : <CaretDown size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                                  )}
                              </button>

                              {/* Level 4+ (if subCategory has more children, render recursively) */}
                              {expandedCategories.has(subCategory.id) &&
                                subCategory.categoryOptions && (
                                  <div role="group" className="bg-bg-brand-secondary-default">
                                    {renderCategory(subCategory, level + 2)}
                                  </div>
                                )}
                            </div>
                          ))}
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Scrollbar styles for webkit browsers (Chrome, Safari, Edge) */}
      <style>{`
 .scrollbar-hide::-webkit-scrollbar {
 width: 8px;
 height: 8px;
 }
 .scrollbar-hide::-webkit-scrollbar-track {
 background: transparent;
 }
 .scrollbar-hide::-webkit-scrollbar-thumb {
 background: transparent;
 border-radius: 4px;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb {
 background: #CBD5E1;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb:hover {
 background: #94A3B8;
 }`}</style>

      <div
        className={`bg-bg-brand-secondary-default w-full md:w-[300px] lg:w-[350px] ${showMobileMenu || isZoomedIn ? "flex h-screen" : "hidden md:flex md:h-full"} border-r-0 md:border-r border-gray-200 flex-col flex-shrink-0 min-h-0`}
        style={
          showMobileMenu || isZoomedIn
            ? {
                height: "100vh",
                maxHeight: "100vh",
              }
            : undefined
        }
        role={showMobileMenu ? "dialog" : undefined}
        aria-modal={showMobileMenu ? "true" : undefined}
        aria-label={showMobileMenu ? "Categories menu" : undefined}
      >
        {/* Mobile Header with Close Button */}
        {(showMobileMenu || isZoomedIn) && (
          <div className={`${!isZoomedIn ? "md:hidden" : ""} px-4 md:px-6 pt-3 pb-2 border-b border-gray-200 flex-shrink-0`}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="label-m-semibold text-fg-neutral-strong">
                {i18n.get("categories")}
              </h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 hover:bg-bg-brand-secondary-hover rounded-lg transition-colors"
                aria-label="Close categories menu"
              >
                <XCircle size={24} className="text-fg-neutral-strong" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="px-4 md:px-6 py-4 flex-shrink-0">
          <div className="relative">
            <label htmlFor="category-search" className="sr-only">
              {searchPlaceholder} 
            </label>
            <input
              id="category-search"
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full h-12 pl-4 pr-10 py-2 md:py-3 text-sm md:text-[13px] border border-gray-400 rounded-lg bg-bg-brand-secondary-default text-gray-900 placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm ? (
              <button
                onClick={() => {
                  const eventName = getEventName("accessibilityGuideSearchCleared");
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {});
                  setSearchTerm("");
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-neutral-strong hover:text-fg-neutral-strong transition-colors p-1"
                aria-label="Clear search"
              >
                <XCircle size={22} aria-hidden="true" />
              </button>
            ) : (
              <MagnifyingGlass
                size={22}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-neutral-strong"
                aria-hidden="true"
              />
            )}
          </div>
        </div>

        {/* Filter Slot - optional filter buttons passed by parent (e.g. on space detail page) */}
        {filterSlot && (
          <div className="px-4 md:px-6 pb-3 flex items-center gap-2 flex-shrink-0">
            {filterSlot}
          </div>
        )}

        {/* Navigation Categories */}
        <nav
          role="tree"
          aria-label="Space categories"
          className="overflow-y-auto flex-1 relative scrollbar-hide hover:scrollbar-show"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
          }}
          onKeyDown={handleTreeKeyDown}
          onMouseEnter={(e) => {
            e.currentTarget.style.scrollbarColor = "#CBD5E1 transparent";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.scrollbarColor = "transparent transparent";
          }}
        >
          <div className="pb-20" style={{ paddingBottom: "300px" }}>
            {reviewTags.map((category) => renderCategory(category))}
          </div>
        </nav>
      </div>
    </>
  );
}

export default CategorySidebar;
